import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import dayjs from "dayjs";
import AddQuizDialog from "./AddQuizDialog";
import ShowQuizes from "./ShowQuizes";
import { TextField } from "@mui/material";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { Info } from "react-feather";

const Quiz = ({
  handleQuizSubmit,
  removeQuiz,
  goBack,
  setPassingPercentage,
  passingPercentage,
  updateQuizContent, // Fixed typo from previous version
  isEditMode,
  activeQuiz,
  addQuizToContent,
  updateQuizQuestion,
  finalAssessment,
  setFinalAssessment,
  quizTimer,
  setQuizTimer,
  scheduledAttempt,
  setScheduledAttempt,
  startDate,
  setStartDate,
  startTime,
  setStartTime,
  endDate,
  setEndDate,
  endTime,
  setEndTime,
  isProctored,
  setIsProctored,
  maxAttempts,
  setMaxAttempts,
  allowMultipleAttempts,
  setAllowMultipleAttempts,
  effectiveCourseId,
  activeSection,
}) => {
  const [openAddquestionModal, setOpenAddquestionModal] = useState(false);
  const [addQuestionBtnCLicked, setAddQuestionBtnCLicked] = useState(false);
  const [quizExists, setQuizExists] = useState(false); // Fixed typo: quizEsixt -> quizExists
  const [passingPercentageError, setPassingPercentageError] = useState(null);
  const [propsError, setPropsError] = useState(null);

  // Log received props and quiz data
  console.log("Quiz - Props received:", {
    effectiveCourseId,
    activeSection,
    activeQuiz,
    isEditMode,
    passingPercentage,
    finalAssessment,
    quizTimer,
    scheduledAttempt,
    startDate,
    startTime,
    endDate,
    endTime,
    isProctored,
  });

  const handleAddQuestion = () => {
    setAddQuestionBtnCLicked(true);

    // Validate passing percentage
    if (!passingPercentage) {
      setPassingPercentageError("Please fill passing percentage");
      return;
    }
    if (passingPercentage > 100) {
      setPassingPercentageError("Passing percentage cannot be more than 100");
      return;
    }
    if (passingPercentage < 0) {
      setPassingPercentageError("Passing percentage cannot be less than 0");
      return;
    }
    setPassingPercentageError(null);

    // Validate props for image uploads
    if (!effectiveCourseId) {
      setPropsError("Course ID is missing. Please ensure a valid course is selected.");
      return;
    }
    if (!activeSection?.sectionId) {
      setPropsError("Section ID is missing. Please ensure a valid section is selected.");
      return;
    }
    setPropsError(null);

    setOpenAddquestionModal(true);
  };

  const handleClose = () => {
    setOpenAddquestionModal(false);
    setAddQuestionBtnCLicked(false);
  };

  useEffect(() => {
    if (activeQuiz && activeQuiz.questions?.length > 0) {
      setQuizExists(true);
      console.log("Quiz - Active quiz data:", activeQuiz);
    } else {
      setQuizExists(false);
    }
  }, [activeQuiz]);

  const handleChange = (e) => {
    setPassingPercentage(e.target.value > 100 ? 100 : e.target.value);
  };

  const updateQuizDetails = () => {
    if (!effectiveCourseId) {
      setPropsError("Course ID is missing. Please ensure a valid course is selected.");
      return;
    }
    if (!activeSection?.sectionId) {
      setPropsError("Section ID is missing. Please ensure a valid section is selected.");
      return;
    }
    setPropsError(null);
    updateQuizContent();
  };

  return (
    <>
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4 border-bottom py-2">
            <h4 className="mb-0">Quiz Questions</h4>
          </div>

          <Row className="g-3">
            {/* Passing Percentage */}
            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium">Passing Percentage</label>
                <Form.Control
                  type="number"
                  id="passing-percentage"
                  placeholder="Enter passing percentage"
                  name="passingPercentage"
                  value={passingPercentage}
                  onChange={handleChange}
                  onWheel={(e) => e.target.blur()}
                  className="text-dark"
                  min="0"
                  max="100"
                />
                {passingPercentageError && (
                  <div className="text-danger mt-1 small">{passingPercentageError}</div>
                )}
              </div>
            </Col>

            {/* Final Assessment */}
            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium d-flex align-items-center">
                  Final Assessment
                  {allowMultipleAttempts && (
                    <Tippy content="Not applicable when Allow Multiple Attempts is enabled — a final assessment is a one-shot evaluation.">
                      <span className="ms-2">
                        <Info size={16} className="text-muted" />
                      </span>
                    </Tippy>
                  )}
                </label>
                <div className="mt-2">
                  <Form.Check
                    name="finalAssessment"
                    onChange={(e) => {
                      setFinalAssessment(e.target.checked);
                    }}
                    checked={!allowMultipleAttempts && finalAssessment}
                    disabled={allowMultipleAttempts}
                    label="Mark as final assessment"
                    id="final-assessment-check"
                  />
                </div>
              </div>
            </Col>

            {/* Quiz Timer */}
            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium">Quiz Timer (Minutes)</label>
                <Form.Control
                  type="number"
                  id="quiz-timer"
                  placeholder="Enter quiz duration"
                  name="quizTimer"
                  value={quizTimer}
                  onChange={(e) => {
                    setQuizTimer(Number(e.target.value));
                  }}
                  onWheel={(e) => e.target.blur()}
                  className="text-dark"
                  min="1"
                />
              </div>
            </Col>

            {/* Allow Multiple Attempts */}
            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium d-flex align-items-center">
                  Allow Multiple Attempts
                  <Tippy content={finalAssessment ? "Not applicable when Final Assessment is enabled." : "Not applicable when Scheduled or Proctored Quiz is enabled."}>
                    <span className="ms-2">
                      <Info size={16} className="text-muted" />
                    </span>
                  </Tippy>
                </label>
                <div className="mt-2">
                  <Form.Check
                    type="switch"
                    id="allow-multiple-attempts-switch"
                    checked={(scheduledAttempt || isProctored || finalAssessment) ? false : allowMultipleAttempts}
                    onChange={(e) => setAllowMultipleAttempts(e.target.checked)}
                    disabled={scheduledAttempt || isProctored || finalAssessment}
                    label={(scheduledAttempt || isProctored || finalAssessment) || !allowMultipleAttempts ? "No Attempt Tracking" : "Multiple Attempts"}
                  />
                </div>
              </div>
            </Col>

            {allowMultipleAttempts && !(scheduledAttempt || isProctored || finalAssessment) && (
              <Col xs={12} md={6} lg={4}>
                <div className="mb-3">
                  <label className="form-label fw-medium">Max Attempts</label>
                  <Form.Control
                    type="number"
                    id="max-attempts"
                    placeholder="Enter max attempts allowed"
                    name="maxAttempts"
                    value={maxAttempts}
                    onChange={(e) => setMaxAttempts(e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    className="text-dark"
                    min="2"
                  />
                </div>
              </Col>
            )}

            {/* Scheduled Quiz Toggle */}
            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium d-flex align-items-center">
                  Scheduled Quiz
                  <Tippy content="If enabled, students can only take the quiz within the specified window.">
                    <span className="ms-2">
                      <Info size={16} className="text-muted" />
                    </span>
                  </Tippy>
                </label>
                <div className="mt-2">
                  <Form.Check
                    type="switch"
                    id="scheduled-quiz-switch"
                    checked={scheduledAttempt}
                    onChange={(e) => setScheduledAttempt(e.target.checked)}
                    label={scheduledAttempt ? "Scheduled" : "Always Available"}
                  />
                </div>
              </div>
            </Col>

            {/* Proctoring Toggle */}
            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium d-flex align-items-center">
                  Proctored Quiz
                  <Tippy content="Enabling proctoring will apply Fullscreen Enforcement, Tab/Window Switch Detection, Copy-Paste & Right-Click Disable, and DevTools/Shortcut Blocking when attempting this quiz.">
                    <span className="ms-2">
                      <Info size={16} className="text-muted" />
                    </span>
                  </Tippy>
                </label>
                <div className="mt-2">
                  <Form.Check
                    type="switch"
                    id="proctoring-switch"
                    checked={isProctored}
                    onChange={(e) => setIsProctored(e.target.checked)}
                    label={isProctored ? "Proctoring Enabled" : "Proctoring Disabled"}
                  />
                </div>
              </div>
            </Col>

            {scheduledAttempt && (
              <>
                {/* Start Date & Time */}
                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-medium text-primary">Access Opens (Start)</label>
                    <Row className="g-2">
                      <Col>
                        <Flatpickr
                          value={startDate}
                          onChange={([date]) => {
                            const formatted = date ? dayjs(date).format("YYYY-MM-DD") : "";
                            setStartDate(formatted);
                          }}
                          className="form-control text-dark"
                          placeholder="Select Start Date"
                          options={{ dateFormat: "Y-m-d" }}
                        />
                      </Col>
                      <Col>
                        <MobileTimePicker
                          value={startTime ? dayjs(startTime, "HH:mm") : null}
                          onChange={(newValue) => {
                            setStartTime(newValue ? newValue.format("HH:mm") : "");
                          }}
                          views={["hours", "minutes"]}
                          openTo="hours"
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              className: "bg-white",
                              sx: {
                                "& .MuiInputBase-root": {
                                  height: "38px",
                                },
                              },
                            },
                          }}
                        />
                      </Col>
                    </Row>
                  </div>
                </Col>

                {/* End Date & Time */}
                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-medium text-danger">Access Closes (End)</label>
                    <Row className="g-2">
                      <Col>
                        <Flatpickr
                          value={endDate}
                          onChange={([date]) => {
                            const formatted = date ? dayjs(date).format("YYYY-MM-DD") : "";
                            setEndDate(formatted);
                          }}
                          className="form-control text-dark"
                          placeholder="Select End Date"
                          options={{ dateFormat: "Y-m-d" }}
                        />
                      </Col>
                      <Col>
                        <MobileTimePicker
                          value={endTime ? dayjs(endTime, "HH:mm") : null}
                          onChange={(newValue) => {
                            setEndTime(newValue ? newValue.format("HH:mm") : "");
                          }}
                          views={["hours", "minutes"]}
                          openTo="hours"
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              className: "bg-white",
                              sx: {
                                "& .MuiInputBase-root": {
                                  height: "38px",
                                },
                              },
                            },
                          }}
                        />
                      </Col>
                    </Row>
                  </div>
                </Col>
              </>
            )}
          </Row>

          {propsError && (
            <div className="text-danger mt-3">{propsError}</div>
          )}

          <ShowQuizes
            quiz={activeQuiz}
            removeQuiz={removeQuiz}
            updateQuizQuestion={updateQuizQuestion}
            isEditMode={isEditMode}
            effectiveCourseId={effectiveCourseId}
            activeSection={activeSection}
          />

          <div className="d-flex justify-content-between mt-5">
            {quizExists ? (
              isEditMode ? (
                <Button onClick={updateQuizDetails}>Update</Button>
              ) : (
                <Button onClick={() => addQuizToContent()}>Done</Button>
              )
            ) : null}

            <Button onClick={handleAddQuestion}>Add Question</Button>
          </div>
        </Card.Body>
      </Card>
      {openAddquestionModal ? (
        <AddQuizDialog
          show={openAddquestionModal}
          handleClose={handleClose}
          handleQuizSubmit={(quiz) => {
            console.log("Quiz - handleQuizSubmit:", quiz);
            handleQuizSubmit(quiz);
          }}
          setAddQuestionBtnCLicked={setAddQuestionBtnCLicked}
          activeQuiz={activeQuiz}
          effectiveCourseId={effectiveCourseId}
          activeSection={activeSection}
          editQuiz={false} // Default to false, set to true when editing
          quiz={null} // Pass quiz data when editing
          updateQuizQuestion={updateQuizQuestion}
        />
      ) : null}
    </>
  );
};

export default Quiz;